import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from 'features/common/hooks/useColumnSelection';
import { OrderProps } from '../../types';
import {
	PunchupTableContainer,
	PunchupHeaderRow,
	PunchupColumnHeader,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { PunchupLoadMore } from 'features/common/components/Punchup/composites/tables/PunchupLoadMore';
import { PunchupTableBody } from './PunchupTableBody';

interface PunchupOrdersTableProps extends OrderProps {
	visibleDefs: ColumnDef[];
	gridColumns: string;
	onResizeColumn: (key: string, width: number) => void;
	/** True while the initial fetch is in flight (data === undefined). */
	isLoading?: boolean;
	/** True while a next-page fetch is in flight (paginating). */
	isFetchingNextPage?: boolean;
	/** True while an already-loaded table re-fetches — dims the table with a spinner overlay. */
	isRefetching?: boolean;
	/** True when a search term or filter is active — drives the body's "no results" empty copy. */
	hasActiveQuery?: boolean;
}

/**
 * Table + load-more for the Transactions sub-page. Returns a Fragment so the
 * parent page can wrap this and the controls row inside one
 * `PunchupTableLayout` for right-edge alignment.
 */
export const PunchupOrdersTable: FC<PunchupOrdersTableProps> = ({
	ordersQueryObject,
	visibleDefs,
	gridColumns,
	onResizeColumn,
	isLoading,
	isFetchingNextPage,
	isRefetching,
	hasActiveQuery,
}) => {
	const { t } = useTranslation();
	const { fetchNextPage, hasNextPage } = ordersQueryObject;

	return (
		<>
			<PunchupTableContainer columns={gridColumns} isRefetching={isRefetching}>
				<thead>
					<PunchupHeaderRow>
						{visibleDefs.map((col) => (
							<PunchupColumnHeader key={col.key} onResize={(w) => onResizeColumn(col.key, w)}>
								{t(col.labelKey)}
							</PunchupColumnHeader>
						))}
					</PunchupHeaderRow>
				</thead>
				<PunchupTableBody
					ordersQueryObject={ordersQueryObject}
					visibleDefs={visibleDefs}
					isLoading={isLoading}
					hasActiveQuery={hasActiveQuery}
				/>
			</PunchupTableContainer>

			<PunchupLoadMore isDisabled={!hasNextPage} isLoading={isFetchingNextPage} handleClick={() => fetchNextPage()} />
		</>
	);
};
